<template>
  <div class="widget-body">
    <div v-if="isWatchlistFilterActive && !watchlistItems.length" class="notice">
      {{ i18n('messages.watchlist-empty') }}
    </div>
    <splitpanes v-else :horizontal="layout === 'horizontal'" @resized="resizedPanes">
      <pane min-size="20" :size="splitPanesSize[0]">
        <c-loader v-if="isHeadlinesLoading" />
        <div v-else-if="!headlines.length" class="notice">{{ i18n('messages.no-headlines') }}</div>
        <b-table
          v-else
          bordered
          dark
          :fields="headlinesFields"
          head-variant="dark"
          hover
          :items="headlines"
          ref="headlinesTable"
          selectable
          select-mode="single"
          selected-variant="selected"
          small
          striped
          @row-clicked="onHeadlineSelected"
          @keydown.enter="onHeadlineSelected"
          :aria-label="i18n('widget-title')"
        >
          <template #head(date)>{{ i18n('labels.date') }}</template>
          <template #head(time)>{{ i18n('labels.time') }}</template>
          <template #head(headline)>{{ i18n('labels.headline') }}</template>
          <template #cell(headline)="row">
            <div v-html="row.item.headline" />
            <span class="sr-only">{{ i18n('messages.screenReaderLink') }}</span>
          </template>
        </b-table>
      </pane>
      <pane min-size="20" v-if="layout !== 'popup'" ref="storyPane" :size="splitPanesSize[1]" />
    </splitpanes>

    <!-- story container will be moved to a split pane or to dialog, depending on layout configuration -->
    <div ref="storyContainer">
      <div class="history-buttons" ref="story">
        <v-button
          v-if="newsHistory.length > 1"
          @click="onOriginStory"
          :aria-label="i18n('labels.doubleArrow')"
          ref="doubleLeft"
          ><font-awesome-icon :icon="['fas', 'angle-double-left']"
        /></v-button>
        <v-button
          v-if="newsHistory.length"
          @click="onPrevStory"
          :aria-label="i18n('labels.singleArrow')"
          ref="singleLeft"
          ><font-awesome-icon :icon="['fas', 'angle-left']"
        /></v-button>
      </div>
      <div class="story" v-if="activeHeadline && story">
        <div>{{ activeHeadline.date }} {{ activeHeadline.time }}</div>
        <div class="headline" v-html="activeHeadline.headline"></div>
        <div v-html="markKeywords(story, true)" @click="onStoryClick"></div>
      </div>
    </div>

    <v-dialog class="news-story" ref="storyDialog" />
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import VDialog from '@base/VDialog';
import widgetCommons from '@mixins/widgetCommons';
import { API } from '@state/api';
import { Splitpanes, Pane } from 'splitpanes';
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faAngleLeft, faAngleDoubleLeft } from '@fortawesome/pro-light-svg-icons';
fontAwesomeLibrary.add(faAngleLeft, faAngleDoubleLeft);

export default {
  name: 'w-news-widget',
  mixins: [widgetCommons],
  components: {
    CLoader,
    Pane,
    Splitpanes,
    VDialog
  },
  props: {
    disableSymbolLinks: Boolean,
    disableTopicLinks: Boolean,
    layout: {
      type: String,
      default: 'horizontal'
    },
    maxTags: {
      type: Number,
      default: 1
    },
    symbols: {
      type: Array,
      required: true
    },
    keywords: {
      type: Array,
      default: () => []
    },
    topics: {
      type: Array,
      default: () => []
    },
    isWatchlistFilterActive: {
      type: Boolean,
      default: false
    },
    watchlistItems: {
      type: Array,
      default: () => []
    },
    watchlistSymbols: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      autoSelectFirstHeadline: false,
      cachedStories: {},
      headlines: [],
      headlinesFields: ['date', 'time', 'headline'],
      i18nKey: 'news',
      isHeadlinesLoading: true,
      maxHeadlines: 200,
      newsHistory: [],
      story: '',
      subscriptionId: null
    };
  },
  computed: {
    languages() {
      return this.$store.state.applicationSettings.general.settings.newsLanguage;
    },
    streamFilter() {
      const filters = ['la=' + this.languages];
      if (this.symbols.length) filters.push('ig=' + this.symbols);
      if (this.topics.filter(t => t.toUpperCase() !== 'ALL').length) {
        filters.push('rc=' + this.topics.map(topic => 'atc=' + topic).join('|'));
      }
      if (this.keywords.length) filters.push('co=' + this.keywords);
      return filters.join(' ');
    },
    timezone() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    },
    activeHeadline: {
      get: function() {
        return this.$store.state.widget[this.wid].activeHeadline;
      },
      set: function(newValue) {
        this.$store.commit(`widget/${this.wid}/ACTIVE_HEADLINE_UPDATED`, newValue);
      }
    },
    splitPanesSize: {
      get: function() {
        return this.$store.state.widget[this.wid].splitPanesSize;
      },
      set: function(newValue) {
        this.$store.commit(`widget/${this.wid}/SPLIT_PANES_SIZE_CHANGED`, newValue);
      }
    }
  },
  watch: {
    activeHeadline(headline) {
      // programmatic row deselection is not available in bootstrap-vue v2.0.0, let's hack our way through
      if (this.headlines.length) {
        if (this.$refs.headlinesTable) this.$refs.headlinesTable.selectedRows = []; // this forces bootstrap table to reevaluate selected state on item._rowVariant change
        this.headlines.forEach(listHeadline => {
          if (headline && headline.id === listHeadline.id) listHeadline._rowVariant = 'selected';
          else listHeadline._rowVariant = '';
        });
      }
      if (headline) {
        this.fetchStory(headline);
      }
    },
    isWatchlistFilterActive() {
      if (!this.isWatchlistFilterActive) {
        this.$nextTick(this.insertReadingPane);
      }
    },
    layout(newVal, oldVal) {
      if (oldVal === 'popup') {
        this.$refs.storyDialog.close();
      }
      if ([newVal, oldVal].includes('popup')) this.$nextTick(this.insertReadingPane);
    },
    streamFilter() {
      this.subscribeStreaming();
    },
    timezone() {
      this.subscribeStreaming();
    },
    watchlistItems() {
      if (!this.isWatchlistFilterActive || this.watchlistItems.length > 0) {
        this.$nextTick(this.insertReadingPane);
      }
    },
    'newsHistory.length': {
      handler(value) {
        value > 1
          ? this.$nextTick(() => {
              this.$refs.doubleLeft.focus();
            })
          : this.$nextTick(() => {
              this.$refs.singleLeft.focus();
            });
      }
    }
  },
  methods: {
    resizedPanes(panes) {
      this.splitPanesSize = [panes[0].size, panes[1].size]; //there are 2 panes so we store their sizes after splitter location changes
    },
    addNews(data) {
      const headline = data.payload.news[0];
      this.headlines.unshift(this.parseHeadline(headline));
      if (this.headlines.length > this.maxHeadlines) this.headlines.pop();
    },
    fetchSearch(newsId) {
      this.activeHeadline = null;
      API.Rest.get(
        `/news/headlines?languages=${this.languages}&fids=id,article,headline,date,time&bracketSearch=${newsId}&service=standard`
      )
        .catch(error => {
          console.error(error);
        })
        .then(response => {
          // console.warn(response.data.data);
          this.activeHeadline = this.parseHeadline(response.data.data[0]);
        });
    },
    fetchStory(headline) {
      if (this.cachedStories[headline.id]) this.story = this.cachedStories[headline.id];
      else {
        // don't show previous story while the new one is being fetched
        this.story = '';
        API.Rest.get(`/news/stories?id=${headline.id}&languages=${this.languages}&service=standard`)
          .catch(error => {
            console.error(error);
          })
          .then(response => {
            this.story = this.parseStory(response.data.data[0].a);
            this.cachedStories[headline.id] = this.story;
          });
      }
    },
    insertReadingPane() {
      if (this.layout === 'popup') {
        this.$refs.storyDialog.$el.querySelector('.dialog-content').appendChild(this.$refs.storyContainer);
      } else {
        this.$refs.storyPane.$el.appendChild(this.$refs.storyContainer);
      }
    },
    markKeywords(str, asHTML) {
      if (!this.keywords.length) return str;
      const rx = new RegExp('\\b(?:' + this.keywords.join('|') + ')\\b', 'gi');
      if (asHTML) {
        const domWrapper = document.createElement('div');
        domWrapper.innerHTML = str;
        const treewalker = document.createTreeWalker(domWrapper, NodeFilter.SHOW_TEXT);
        while (treewalker.nextNode()) {
          treewalker.currentNode.nodeValue = treewalker.currentNode.nodeValue.replace(rx, '\0$&\0');
        }
        str = domWrapper.innerHTML;
      } else {
        str = str.replace(rx, '\0$&\0');
      }
      return str.replace(/\0(.+?)\0/g, (...matches) => {
        return `<span class="keyword-${this.keywords.findIndex(k => k.toLowerCase() === matches[1].toLowerCase()) +
          1}">${matches[1]}</span>`;
      });
    },
    onHeadlineSelected(headline) {
      this.newsHistory = [];
      this.activeHeadline = headline;
      if (this.layout === 'popup' && !this.$refs.storyDialog.visible) {
        const vm = this;
        this.$refs.storyDialog.open({
          get header() {
            return vm.i18n('storyDialog.title');
          },
          insertTo: '^[class$="__workspace"]',
          type: 'window'
        });
      }
    },
    onOriginStory() {
      this.activeHeadline = this.newsHistory[0];
      this.newsHistory = [];
    },
    onPrevStory() {
      this.activeHeadline = this.newsHistory.pop();
    },
    onStoryClick(ev) {
      const el = ev.target.closest('A');
      if (el && el.href.startsWith('javascript:')) {
        if (el.className === 'ric') {
          const ric = el.innerText;
          if (!this.symbols.includes(ric)) {
            if (this.symbols.length < this.maxTags) {
              this.$emit('symbolsChanged', [...this.symbols, ric]);
            }
          }
        } else if (el.className === 'news') {
          this.newsHistory.push(this.activeHeadline);
          const term = el.innerText;
          // search for a single story, if the link starts with an "n" or "."
          if ('n.'.includes(term[0])) this.fetchSearch(term);
          // otherwise take the content of the link and set it as a topic code
          else {
            this.$emit('symbolsChanged', []);
            this.$emit('topicsChanged', [{ text: term }]);
            this.$emit('keywordsChanged', []);
            this.$emit('filterChanged', false);
          }
        }
        ev.preventDefault();
      }
    },
    parseHeadline(headline) {
      // let the browser html parser escape tags for us
      const domWrapper = document.createElement('div');
      domWrapper.innerText = headline.headline;
      headline.headline = this.markKeywords(domWrapper.innerHTML);
      return headline;
    },
    parseStory(story) {
      // linkify urls
      // the regex matches anything that looks like a url, it also captures a character before the matched url in order to check if the url is a tag attribute value, in this case do not linkify that url
      story = story.replace(/(.)(https?:\/\/|www\.)([/+~%\w?#@&=;:_.,-]+)/g, (...matches) => {
        if ('"='.includes(matches[1])) return matches[0];
        const url = (matches[2] === 'www.' ? 'http://' : '') + matches[2] + matches[3];
        return matches[1] + `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
      story = story.replace(
        RegExp(`\\[(${this.disableTopicLinks ? '[n.]' : ''}[\\w\\d/-]+)\\]`, 'g'),
        '[<a class="news" href="javascript:;">$1</a>]'
      );
      if (!this.disableSymbolLinks) {
        story = story.replace(/&lt;([^&]*[#=.][^&]*)&gt;/g, '&lt;<a class="ric" href="javascript:;">$1</a>&gt;');
      }
      return story;
    },
    removeNews(data) {
      const headline = data.payload.news[0];
      // eslint-disable-next-line
      this.headlines.splice(this.headlines.findIndex(listedHeadline => listedHeadline.id === headline.id), 1);
    },
    setHeadlines(data) {
      const headlines = data.payload.news;
      headlines.forEach(this.parseHeadline);
      this.headlines = headlines;
      this.isHeadlinesLoading = false;
      if (headlines.length && this.layout !== 'popup') {
        if (this.autoSelectFirstHeadline)
          this.$nextTick(() => this.$refs.headlinesTable.$el.querySelector('tbody tr').click());
        else if (this.activeHeadline) {
          let selHeadline = this.headlines.findIndex(h => this.activeHeadline.id === h.id);
          if (selHeadline >= 0) {
            this.$nextTick(() => {
              let headlineElement = this.$refs.headlinesTable.$el.querySelector(`tbody tr:nth-child(${++selHeadline})`);
              headlineElement.click();
              if (this.$route && this.$route.name === 'widget-popout') headlineElement.scrollIntoView();
            });
          }
        }
      } else {
        this.newsHistory = [];
        this.story = '';
      }
      this.autoSelectFirstHeadline = true;
    },
    subscribeStreaming() {
      if (!this.isWatchlistFilterActive || this.watchlistItems.length > 0) {
        this.isHeadlinesLoading = true;
        if (this.subscriptionId !== null) {
          this.streamingUnsubscribe();
        }
        // if watchlist filter is active and a watchlist is selected, but the symbols list is empty, then there's a constituents request most probably running at this moment, which tries to get the full symbols list
        if (this.isWatchlistFilterActive && !this.watchlistSymbols.length) return;
        this.subscriptionId = API.Streaming.News.subscribe(
          {
            fids: [
              'id', // required!
              'date',
              'time',
              'language',
              'headline',
              'instruments',
              'nameditems',
              'productcodes',
              'topiccodes',
              'permissions',
              'jtime'
            ],
            timezone: this.timezone,
            tonumber: this.maxHeadlines,
            history: 'all',
            encoding: 'utf-8',
            filter: this.streamFilter
          },
          // Subscribe Callback
          (_data, err) => {
            if (err) {
              console.error('News streaming subscribe error', err);
            }
          },
          this.addNews,
          this.updateNews,
          this.setHeadlines, // snapshot
          this.removeNews
        );
      }
    },
    streamingUnsubscribe() {
      if (this.subscriptionId !== null) API.Streaming.News.unsubscribe(this.subscriptionId);
    },
    updateNews(data) {
      const headline = data.payload.news[0];
      this.parseHeadline(headline);
      const index = this.headlines.findIndex(listedHeadline => listedHeadline.id === headline.id);
      if (~index) {
        const rowComponent = this.$refs.headlinesTable.$refs.itemRows[index];
        Object.assign(this.headlines[index], headline);
        if (rowComponent) {
          const rowEl = rowComponent.$el;
          rowEl.classList.remove('update'); // stop any running animation
          rowEl.classList.add('update');
          const ev = rowEl.addEventListener('animationend', () => {
            rowEl.classList.remove('update');
            rowEl.removeEventListener('animationend', ev);
          });
        }
      }
      if (this.cachedStories[headline.id]) this.cachedStories[headline.id] = '';
      if (this.activeHeadline && this.activeHeadline.id === headline.id) this.activeHeadline = headline;
    }
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.$refs.storyDialog.visible) {
      this.$refs.storyDialog.close();
    }
  },
  mounted() {
    // move story container to its appropriate component depending on layout configuration
    this.insertReadingPane();
    if (!this.activeHeadline) this.autoSelectFirstHeadline = true;
    API.Streaming.connect({
      timeout: 10000,
      monitor: {
        interval: 10000,
        timeout: 5000
      },
      visibilityMonitorEnabled: true
    })
      .catch(error => {
        console.error(error); // catch api errors
      })
      .then(this.subscribeStreaming);
  }
};
</script>
